<template>
  <div>
    <v-data-table
      :headers="ptsiHeaders"
      :items="productionPlaces"
      :items-per-page="10"
      hide-default-footer
      :loading="loading"
      :loading-text="$t('deforestation.loadingProductionPlaces')"
    >
      <!-- Top toolbar: search + pagination -->
      <template v-slot:top="{}">
        <div class="d-flex mt-5 justify-space-between filter-pro-places">
          <div class="d-flex align-center">
            <v-text-field
              prepend-inner-icon="mdi-magnify"
              outlined
              height="5px"
              :placeholder="$t('search')"
              dense
              :value="search"
              @input="onSearchInput"
              class="shrink"
              clearable
            />
          </div>
          <div class="d-flex mb-5 pagination">
            <div class="py-1 title">{{ from }} - {{ to }} of {{ totalProductionPlaces }}</div>
            <v-btn class="mx-2" fab small outlined color="primary"
              :disabled="options.page <= 1 || loading"
              @click="$emit('page-change', false)">
              <v-icon dark>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn class="mx-2" fab small outlined color="primary"
              :disabled="options.page >= totalPages || loading"
              @click="$emit('page-change', true)">
              <v-icon dark>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn class="mx-2" fab small outlined color="primary" @click="$emit('export-pdf')">
              <v-icon dark>mdi-tray-arrow-down</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Select by risk -->
        <div v-if="manualApproveEligibleOnPage.length" class="pp-quick-select mb-3">
          <div class="d-flex align-center flex-wrap justify-space-between" style="gap:12px;">
            <div class="d-flex align-center flex-wrap" style="gap:12px;">
              <span class="pp-quick-select__label">Select by risk</span>
              <div
                v-if="mediumRiskEligibleOnPage.length"
                role="button" tabindex="0"
                class="pp-risk-checkbox pp-risk-checkbox--yellow"
                :class="{ 'pp-risk-checkbox--checked': isMediumRiskFilterChecked || isMediumRiskFilterIndeterminate }"
                @click="handleRiskFilterClick('medium')"
                @keydown.enter.prevent="handleRiskFilterClick('medium')"
                @keydown.space.prevent="handleRiskFilterClick('medium')"
              >
                <v-checkbox :input-value="isMediumRiskFilterChecked" :indeterminate="isMediumRiskFilterIndeterminate"
                  hide-details color="warning" class="pp-risk-checkbox__input mt-0 pt-0 flex-shrink-0" />
                <span class="pp-risk-checkbox__label">Medium Risk</span>
                <span class="pp-quick-select-chip__count">{{ mediumRiskEligibleOnPage.length }}</span>
              </div>
              <div
                v-if="highRiskEligibleOnPage.length"
                role="button" tabindex="0"
                class="pp-risk-checkbox pp-risk-checkbox--red"
                :class="{ 'pp-risk-checkbox--checked': isHighRiskFilterChecked || isHighRiskFilterIndeterminate }"
                @click="handleRiskFilterClick('high')"
                @keydown.enter.prevent="handleRiskFilterClick('high')"
                @keydown.space.prevent="handleRiskFilterClick('high')"
              >
                <v-checkbox :input-value="isHighRiskFilterChecked" :indeterminate="isHighRiskFilterIndeterminate"
                  hide-details color="error" class="pp-risk-checkbox__input mt-0 pt-0 flex-shrink-0" />
                <span class="pp-risk-checkbox__label">High Risk</span>
                <span class="pp-quick-select-chip__count">{{ highRiskEligibleOnPage.length }}</span>
              </div>
              <div
                role="button" tabindex="0"
                class="pp-risk-checkbox pp-risk-checkbox--primary"
                :class="{ 'pp-risk-checkbox--checked': pageSelectAll || pageIndeterminate }"
                @click="handleRiskFilterClick('all')"
                @keydown.enter.prevent="handleRiskFilterClick('all')"
                @keydown.space.prevent="handleRiskFilterClick('all')"
              >
                <v-checkbox :input-value="pageSelectAll" :indeterminate="pageIndeterminate"
                  hide-details color="primary" class="pp-risk-checkbox__input mt-0 pt-0 flex-shrink-0" />
                <span class="pp-risk-checkbox__label">All on Page</span>
                <span class="pp-quick-select-chip__count">{{ manualApproveEligibleOnPage.length }}</span>
              </div>
            </div>
            <button v-if="manualApproveCheckbox.length" type="button"
              class="pp-quick-select-chip pp-quick-select-chip--clear"
              @click="clearManualApproveSelection">
              Clear selection ({{ manualApproveCheckbox.length }})
            </button>
          </div>
        </div>

        <!-- Bulk selection action bar -->
        <v-slide-y-transition>
          <div v-if="manualApproveCheckbox.length > 0" class="pp-bulk-bar mb-3">
            <div class="pp-bulk-bar__inner">
              <div class="d-flex align-center flex-wrap" style="gap:12px;">
                <div class="pp-bulk-bar__count-badge">
                  <v-icon size="18" color="white">mdi-checkbox-marked-circle</v-icon>
                  <span>{{ manualApproveCheckbox.length }} selected</span>
                </div>
                <div v-if="manualApproveBulkSummary.medium" class="pp-bulk-bar__risk-pill pp-bulk-bar__risk-pill--yellow">
                  {{ manualApproveBulkSummary.medium }} medium risk
                </div>
                <div v-if="manualApproveBulkSummary.high" class="pp-bulk-bar__risk-pill pp-bulk-bar__risk-pill--red">
                  {{ manualApproveBulkSummary.high }} high risk
                </div>
              </div>
              <ManualApproveButton :count="manualApproveCheckbox.length" @click="openBulkManualApproveModal" />
            </div>
          </div>
        </v-slide-y-transition>
      </template>

      <!-- Checkbox header -->
      <template v-slot:header.select>
        <v-checkbox
          :input-value="pageSelectAll"
          :indeterminate="pageIndeterminate"
          :disabled="manualApproveEligibleOnPage.length === 0"
          hide-details color="primary" class="mt-0 pt-0"
          @change="togglePageSelectAll"
        />
      </template>

      <!-- Body -->
      <template v-slot:body="{ items }">
        <tbody v-if="!items.length">
          <tr class="v-data-table__empty-wrapper">
            <td :colspan="ptsiHeaders.length" class="text-center py-6">
              <template v-if="loading">
                <v-progress-circular indeterminate color="primary" size="24" width="2" class="mr-2" />
                {{ $t('deforestation.loadingProductionPlaces') }}
              </template>
              <template v-else>{{ $vuetify.lang.t('$vuetify.noDataText') }}</template>
            </td>
          </tr>
        </tbody>
        <tbody v-else :style="loading ? 'opacity: 0.5; pointer-events: none;' : ''">
          <!-- eslint-disable vue/no-v-for-template-key-on-child -->
          <template v-for="item in items">
            <tr
              :key="`ptsi-row-${item.id}`"
              :class="{ 'pp-row--selected': manualApproveCheckbox.includes(item.id) }"
              @mouseover="selectedItem = item"
              @mouseleave="selectedItem = false"
            >
              <!-- Select checkbox -->
              <td class="text-center" style="width:48px;">
                <v-checkbox
                  v-if="canManualApproveFarm(item)"
                  :input-value="manualApproveCheckbox.includes(item.id)"
                  hide-details color="primary" class="mt-0 pt-0"
                  @change="val => toggleManualApproveFarm(item, val)"
                  @click.stop
                />
              </td>
              <!-- Lot ID (NACCU/Kenya only) -->
              <td class="text-truncate" v-if="showLotId">
                {{ item.lotId || 'N/A' }}
              </td>
              <!-- Farm name / ID -->
              <td class="text-truncate">
                {{ item.farm.farmName || 'N/A' }}<br />
                <span class="text-caption text--secondary">{{ item.farm.id }}</span>
              </td>
              <!-- Producer name -->
              <td class="text-truncate">
                {{ item.farm.userDdsAssoc.firstName }} {{ item.farm.userDdsAssoc.lastName }}
              </td>
              <!-- Address -->
              <td class="text-truncate" style="max-width:300px;">
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{ item.farm.address }}</span>
                  </template>
                  <span>{{ item.farm.address }}</span>
                </v-tooltip>
              </td>
              <!-- Merged compliance status -->
              <td class="text-center">
                <div class="d-flex align-center justify-center">
                  <v-tooltip bottom max-width="360">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small rounded depressed min-width="160" height="34"
                        :class="mergedProductionPlaceStatusBadgeClass(item)"
                        v-bind="attrs" v-on="on"
                        @click.stop="openTrafficLightDialog(item)">
                        {{ mergedProductionPlaceStatusLabel(item) }}
                      </v-btn>
                    </template>
                    <span>{{ mergedProductionPlaceStatusTooltip(item) }}</span>
                  </v-tooltip>
                  <v-tooltip top color="primary" max-width="350"
                    v-if="item.diligenceReportProductionPlaceArray && item.diligenceReportProductionPlaceArray[0] && item.diligenceReportProductionPlaceArray[0].id">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on"
                        @click="openHistoryModal(item)" color="primary" class="ml-2"
                        style="min-width:34px;width:34px;height:34px;">
                        <img src="/img/history.svg" alt="History" style="width:20px;height:20px;" />
                      </v-btn>
                    </template>
                    <span>{{ $t('dueDiligence.viewStatusHistory') }}</span>
                  </v-tooltip>
                </div>
              </td>
              <!-- Actions -->
              <td>
                <div class="d-flex align-center">
                  <v-tooltip top color="primary" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="toggleExpand(item.id)" color="primary">
                        <v-icon>{{ isExpanded(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ isExpanded(item.id) ? $t('dueDiligence.hideDetails') : $t('dueDiligence.showDetails') }}</span>
                  </v-tooltip>

                  <v-menu>
                    <template v-slot:activator="{ on: menu, attrs }">
                      <v-tooltip top color="00BD73" max-width="350">
                        <template v-slot:activator="{ on: tooltip }">
                          <v-btn x-small fab v-bind="attrs" v-on="{ ...menu, ...tooltip }" color="primary"
                            style="margin-left:8px;"
                            @click="onReportButtonClick(item.deforestationStatus)">
                            <img src="/img/id/todo-list.svg" style="width:34px;height:34px;border-radius:50%;" />
                          </v-btn>
                        </template>
                        <span>{{ $t('dueDiligence.viewReport') }}</span>
                      </v-tooltip>
                    </template>
                    <v-list v-if="item.deforestationStatus">
                      <v-list-item
                        v-for="(menu, i) in getMenusForReport(item.deforestationInfo && item.deforestationInfo.deforestationReport)"
                        :key="i">
                        <v-list-item-title class="cursor-pointer" @click="$emit('show-model', menu.id, item)">
                          {{ menu.title }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <v-menu location="start">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" style="margin-left:8px;">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense style="min-width:150px;">
                      <v-list-item @click="openViewFarmModal(item)">
                        <v-list-item-title>{{ $t('dueDiligence.viewFarm') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="canManualApproveFarm(item)" @click="openManualApproveModal(item)">
                        <v-list-item-title>Manual Approve</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>

            <!-- Expanded row -->
            <tr v-if="isExpanded(item.id)" :key="`ptsi-exp-${item.id}`" class="expanded-row">
              <td :colspan="showLotId ? 7 : 6" class="pa-4" style="background-color:#f5f5f5;">
                <v-row class="mb-3">
                  <!-- Location status -->
                  <v-col cols="12" md="4">
                    <div class="detail-section pa-4" style="background:white;border-radius:4px;">
                      <h4 class="mb-3">
                        {{ $t('dueDiligence.locationStatus') }}
                        <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon class="primary--text" style="color:#0EBF67" size="small" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                          </template>
                          <span>{{ $t('dueDiligence.locationStatusTT') }}</span>
                        </v-tooltip>
                      </h4>
                      <div v-if="hasLocationError(item) || hasAreaTooLargeError(item)">
                        <div v-if="hasLocationError(item)" class="mb-3">
                          <div class="d-flex align-center mb-1">
                            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                            <span class="error--text font-weight-bold">{{ $t('dueDiligence.locationError') }}</span>
                          </div>
                          <div class="ml-8 text-caption error--text">{{ $t('dueDiligence.countryMismatchError') }}</div>
                        </div>
                        <div v-if="hasAreaTooLargeError(item)" class="mb-3">
                          <div class="d-flex align-center mb-1">
                            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                            <span class="error--text font-weight-bold">{{ $t('dueDiligence.geofenceError') }}</span>
                          </div>
                          <div class="ml-8 text-caption error--text">{{ $t('dueDiligence.circularFarmAreaTooLarge') }}</div>
                        </div>
                      </div>
                      <div v-else class="d-flex align-center">
                        <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                        <span>{{ $t('dueDiligence.locationVerified') }}</span>
                      </div>
                    </div>
                  </v-col>

                  <!-- EUDR status + merged -->
                  <v-col cols="12" md="4">
                    <div class="detail-section pa-4" style="background:white;border-radius:4px;">
                      <h4 class="mb-3">
                        {{ $t('deforestation.EUDRDeforestationStatus') }}
                        <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon class="primary--text" style="color:#0EBF67" size="small" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                          </template>
                          <span>{{ getEUDRDeforestationStatus(item.deforestationStatus || item.errorStatus || '').text }}</span>
                        </v-tooltip>
                      </h4>
                      <div>
                        <div class="mb-2 d-flex align-center">
                          <v-tooltip top color="black" max-width="350" open-on-hover>
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-if="getEUDRDeforestationStatus(item.deforestationStatus || item.errorStatus).text !== 'N/A'"
                                v-bind="attrs" v-on="on" class="d-inline-flex status-badge-tooltip-wrap">
                                <v-btn small rounded min-width="200" depressed
                                  :class="[getEUDRDeforestationStatus(item.deforestationStatus || item.errorStatus).class, 'non-clickable']"
                                  height="34" tabindex="-1">
                                  {{ getEUDRDeforestationStatus(item.deforestationStatus || item.errorStatus).text }}
                                </v-btn>
                              </span>
                              <span v-else class="text--secondary">N/A</span>
                            </template>
                            <span>{{ getEUDRDeforestationStatus(item.deforestationStatus || item.errorStatus || '').text }}</span>
                          </v-tooltip>
                          <v-icon v-if="getEUDRDeforestationStatus(item.deforestationStatus || item.errorStatus).text !== 'N/A'"
                            small class="ml-2 primary--text" style="color:#0EBF67">mdi-file-document-outline</v-icon>
                        </div>
                        <div v-if="getDeforestationStatusDate(item)" class="mt-2 text-caption">
                          <div><strong>{{ $t('dueDiligence.dateOfIssue') }}:</strong> {{ formatDate(getDeforestationStatusDate(item)) }}</div>
                          <div v-if="getDeforestationStatusDate(item)">
                            <strong>{{ $t('dueDiligence.dateOfExpiry') }}:</strong> {{ formatDate(getExpiryDate(getDeforestationStatusDate(item))) }}
                          </div>
                        </div>
                        <div class="mt-3 pt-3" style="border-top:1px solid #eee;">
                          <div class="mb-2 d-flex align-center" style="gap:4px;">
                            <span class="text-body-2 font-weight-bold">{{ $t('deforestation.mergedComplianceStatus') }}</span>
                            <v-tooltip top color="black" max-width="280">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" x-small color="primary" style="color:#0EBF67 !important;">mdi-information-outline</v-icon>
                              </template>
                              <span>{{ mergedProductionPlaceStatusTooltip(item) }}</span>
                            </v-tooltip>
                          </div>
                          <v-btn small rounded min-width="200" depressed
                            :class="mergedProductionPlaceStatusBadgeClass(item)" height="34"
                            @click="openTrafficLightDialog(item)">
                            {{ mergedProductionPlaceStatusLabel(item) }}
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <!-- Risk assessment -->
                  <v-col cols="12" md="4">
                    <div class="detail-section pa-4" style="background:white;border-radius:4px;">
                      <h4 class="mb-3">
                        {{ $t('dueDiligence.assessments') }}
                        <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon class="primary--text" style="color:#0EBF67" size="small" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                          </template>
                          <span>{{ $t('dueDiligence.riskAssessmentTT') }}</span>
                        </v-tooltip>
                      </h4>
                      <div class="mb-2"><strong>{{ $t('dueDiligence.riskAssessment') }}</strong></div>
                      <div v-if="item.risk_assessment_status">
                        <v-btn small rounded min-width="150" depressed
                          :class="[getRiskAssessmentStatusClass(item.risk_assessment_status), 'non-clickable']"
                          height="34" class="mb-2">
                          {{ getRiskAssessmentStatusText(item.risk_assessment_status) }}
                          <v-icon small class="ml-2">mdi-file-document-outline</v-icon>
                        </v-btn>
                        <div v-if="item.createdAt" class="mt-2 text-caption">
                          <div><strong>{{ $t('dueDiligence.dateOfSubmission') }}:</strong> {{ formatDate(item.createdAt) }}</div>
                          <div><strong>{{ $t('dueDiligence.dateOfExpiry') }}:</strong> {{ formatDate(getExpiryDate(item.createdAt)) }}</div>
                        </div>
                      </div>
                      <div v-else class="text--secondary">{{ $t('dueDiligence.noRiskAssessment') }}</div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Indigenous & protected areas -->
                <v-row class="mb-3">
                  <v-col cols="12">
                    <div class="detail-section pa-4" style="background:white;border-radius:4px;">
                      <h4 class="mb-3">
                        {{ $t('dueDiligence.indigenousAndProtectedAreas') }}
                        <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon class="primary--text" style="color:#0EBF67" size="small" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                          </template>
                          <span>{{ $t('dueDiligence.indigenousAndProtectedAreasTT') }}</span>
                        </v-tooltip>
                      </h4>
                      <v-row>
                        <v-col cols="12" md="6">
                          <div class="mb-2">
                            <strong>{{ $t('dueDiligence.interceptsProtectedArea') }}</strong>
                          </div>
                          <div class="primary--text">
                            {{ (item.dueDiligenceProductionPlacesPyData && (item.dueDiligenceProductionPlacesPyData.protectedAreaTrans || item.dueDiligenceProductionPlacesPyData.protectedArea)) || 'N/A' }}
                          </div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <div class="mb-2">
                            <strong>{{ $t('dueDiligence.interceptsIndigenousArea') }}</strong>
                          </div>
                          <div class="primary--text">
                            {{ (item.dueDiligenceProductionPlacesPyData && (item.dueDiligenceProductionPlacesPyData.indigenousAreaTrans || item.dueDiligenceProductionPlacesPyData.indigenousArea)) || 'N/A' }}
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>

                <!-- Mitigation documents -->
                <v-row>
                  <v-col cols="12">
                    <div class="detail-section pa-4" style="background:white;border-radius:4px;">
                      <h4 class="mb-3">
                        {{ $t('dueDiligence.mitigationDocuments') }}
                        <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon class="primary--text" style="color:#0EBF67" size="small" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                          </template>
                          <span>{{ $t('dueDiligence.mitigationDocumentsTT') }}</span>
                        </v-tooltip>
                      </h4>
                      <div v-if="getMitigationComment(item)" class="mb-4">
                        <div class="text-subtitle-2 font-weight-bold mb-2" style="color:#424242;">{{ $t('dueDiligence.mitigationComments') }}</div>
                        <div class="text-body-2" style="color:#757575;">{{ getMitigationComment(item) }}</div>
                      </div>
                      <div v-if="getMitigationFiles(item).length > 0">
                        <v-row>
                          <v-col v-for="(file, index) in getMitigationFiles(item)" :key="index" cols="12" md="6" lg="4">
                            <v-card class="mitigation-document-card" elevation="0">
                              <v-card-text class="pa-4">
                                <div class="d-flex flex-column align-center">
                                  <div class="pdf-card-container">
                                    <v-card class="pdf-card d-flex align-center justify-center" elevation="0"
                                      :style="{ backgroundColor: '#E3F2FD', borderRadius: '8px', minWidth: '120px', height: '120px', position: 'relative' }">
                                      <span class="pdf-text primary--text font-weight-bold" style="font-size:24px;">PDF</span>
                                      <v-btn icon small @click="viewDocument(file.file_path)" color="primary"
                                        style="position:absolute;top:8px;right:8px;">
                                        <v-icon>mdi-eye</v-icon>
                                      </v-btn>
                                    </v-card>
                                    <div class="text-caption primary--text mt-2 font-weight-medium text-center">
                                      {{ getFileNameWithoutExtension(file.file_path) }}
                                    </div>
                                  </div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                      <div v-else-if="!getMitigationComment(item)" class="text--secondary">
                        {{ $t('dueDiligence.noMitigationDocuments') }}
                        <div v-if="canManualApproveFarm(item)" class="d-flex mt-3 flex-wrap" style="gap:8px;">
                          <v-btn small depressed color="success" @click="openManualApproveModal(item)">
                            <v-icon small class="mr-1">mdi-check-decagram</v-icon>
                            Manual Approve
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </td>
            </tr>
          </template>
          <!-- eslint-enable vue/no-v-for-template-key-on-child -->
        </tbody>
      </template>

      <template v-slot:footer></template>
    </v-data-table>

    <!-- Status History Modal -->
    <StatusHistoryModal
      :dialog="showHistoryModal"
      :production-place-id="currentProductionPlaceId"
      @close="closeHistoryModal"
    />

    <!-- IYB Traffic Light Panel Dialog -->
    <v-dialog v-if="trafficLightDialog" v-model="trafficLightDialog" max-width="900">
      <v-card class="tl-dialog">
        <div class="tl-dialog__header d-flex align-center px-5 py-3">
          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-bold black--text">
              {{ trafficLightItem && trafficLightItem.farm && trafficLightItem.farm.farmName }}
            </div>
            <div class="text-caption grey--text">{{ $t('deforestation.mergedComplianceStatus') }}</div>
          </div>
          <v-btn icon small @click="trafficLightDialog = false">
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider />
        <v-card-text class="pt-3 pb-4 px-5">
          <div v-if="trafficLightItem" class="pp-status-breakdown mb-4">
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="pp-status-breakdown__card pa-3">
                  <div class="pp-status-breakdown__label mb-2">
                    {{ $t('deforestation.complianceStatusReview') }}
                  </div>
                  <v-btn
                    small
                    rounded
                    depressed
                    min-width="160"
                    height="34"
                    tabindex="-1"
                    class="non-clickable"
                    :class="localDbComplianceDisplay(trafficLightItem).badgeClass"
                  >
                    {{ localDbComplianceDisplay(trafficLightItem).label }}
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="pp-status-breakdown__card pa-3">
                  <div class="pp-status-breakdown__label mb-2">
                    {{ $t('deforestation.EUDRDeforestationStatus') }}
                  </div>
                  <v-btn
                    small
                    rounded
                    depressed
                    min-width="160"
                    height="34"
                    tabindex="-1"
                    class="non-clickable"
                    :class="productionPlaceEudrStatusDisplay(trafficLightItem).class"
                  >
                    {{ productionPlaceEudrStatusDisplay(trafficLightItem).text }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </div>
          <v-divider v-if="trafficLightItem" class="mb-4" />
          <IYBTrafficLightPanel
            v-if="trafficLightItem"
            :key="'tl-panel-' + trafficLightMapKey"
            :traffic-light="trafficLightForPanel(trafficLightItem)"
            :show-mitigation="false"
            :farm="trafficLightItem.farm"
            :farm-type="trafficLightItem.farmType || 'point'"
            :map-active="trafficLightDialog"
            :phase1-coming-soon="false"
            :commodity="trafficLightItem.commodity || reportCommodity"
            :phase1="trafficLightItem.phase1"
            :iyb-data="trafficLightItem.iybData"
            :diligence-report-production-place-id="trafficLightItem.diligenceReportProductionPlaceArray && trafficLightItem.diligenceReportProductionPlaceArray[0] && trafficLightItem.diligenceReportProductionPlaceArray[0].id"
            read-only
          />

          <div
            v-if="trafficLightItem && getFarmMitigationFiles(trafficLightItem).length"
            class="mt-4"
            style="border:1px solid #e0e0e0; border-radius:10px; overflow:hidden;"
          >
            <div class="d-flex align-center px-4 py-3" style="background:#f5f7ff; border-bottom:1px solid #e0e0e0;">
              <v-icon small color="primary" class="mr-2">mdi-paperclip-check</v-icon>
              <span class="text-subtitle-2 font-weight-bold" style="color:#002287;">Submitted Mitigation Documents</span>
              <v-chip x-small color="primary" dark class="ml-2">{{ getFarmMitigationFiles(trafficLightItem).length }}</v-chip>
            </div>
            <div class="pa-3">
              <v-row dense>
                <v-col
                  v-for="(doc, i) in getFarmMitigationFiles(trafficLightItem)"
                  :key="doc.id || doc.file_path || i"
                  cols="12"
                  sm="6"
                >
                  <div class="d-flex align-center pa-3 rounded" style="border:1px solid #e0e7ff; background:#fafcff; gap:10px;">
                    <v-icon color="error" size="28">mdi-file-pdf-box</v-icon>
                    <div style="flex:1; min-width:0;">
                      <div class="text-body-2 font-weight-medium text-truncate">{{ getMitigationFileName(doc.file_path) }}</div>
                      <div v-if="doc.riskMitigationComment" class="text-caption text--secondary text-truncate">
                        {{ doc.riskMitigationComment }}
                      </div>
                    </div>
                    <v-btn icon x-small color="primary" @click="openMitigationFile(doc.file_path)">
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <div v-if="canManualApproveFarm(trafficLightItem)" class="d-flex mt-3" style="gap:8px;">
                <v-btn
                  small
                  depressed
                  color="success"
                  class="flex-grow-1"
                  :loading="trafficLightEvidenceLoading"
                  @click="approveTrafficLightEvidence(trafficLightItem)"
                >
                  <v-icon small class="mr-1">mdi-check-circle</v-icon>
                  Approve Evidence
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- View Farm -->
    <ViewGeoLocation
      v-if="viewFarmDialog && selectedProductionPlace"
      :dialog="viewFarmDialog"
      :farmDetails="selectedProductionPlace"
      :disableEditGeofence="true"
      :diligenceReportId="parseInt(diligenceId, 10)"
      @closeViewGeoLocationModal="closeViewFarmModal"
      @saveViewGeoLocation="closeViewFarmModal"
    />

    <!-- Manual Approve Dialog -->
    <v-dialog v-model="manualApproveModal.open" max-width="640" scrollable persistent>
      <v-card v-if="manualApproveModal.items && manualApproveModal.items.length" class="pp-approve-dialog">
        <div class="pp-approve-dialog__header">
          <div class="pp-approve-dialog__header-icon">
            <v-icon :style="{ color: '#ffffff !important' }" size="22">mdi-check-decagram</v-icon>
          </div>
          <div style="flex:1;min-width:0;">
            <div class="pp-approve-dialog__title">
              {{ manualApproveModal.mode === 'bulk' ? 'Bulk Manual Approval' : 'Manual Approval' }}
            </div>
            <div class="pp-approve-dialog__subtitle">
              Ref {{ manualApproveModal.ref }}
              <span v-if="manualApproveModal.mode === 'bulk'"> · {{ manualApproveModal.items.length }} farm(s)</span>
            </div>
          </div>
          <v-btn icon small @click="closeManualApproveModal">
            <v-icon :style="{ color: '#ffffff !important' }">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4 black--text">
          <div v-if="manualApproveModal.mode === 'bulk'" class="pp-approve-dialog__summary mb-4">
            <div v-if="manualApproveModal.mediumCount" class="pp-approve-dialog__summary-pill pp-approve-dialog__summary-pill--yellow">
              {{ manualApproveModal.mediumCount }} Medium Risk
            </div>
            <div v-if="manualApproveModal.highCount" class="pp-approve-dialog__summary-pill pp-approve-dialog__summary-pill--red">
              {{ manualApproveModal.highCount }} High Risk
            </div>
          </div>
          <div class="text-body-2 mb-4">
            {{ manualApproveModal.mode === 'bulk'
              ? 'Selected farms will be reclassified as Manually Mitigated in Local DB Compliance.'
              : 'This production place will be reclassified as Manually Mitigated in Local DB Compliance.' }}
          </div>
          <div class="mb-4" style="max-height:220px;overflow-y:auto;">
            <v-card v-for="farm in manualApproveModal.farms" :key="farm.id" outlined class="mb-2" style="border-color:#e0e0e0;">
              <div class="d-flex align-center px-3 py-2" style="gap:10px;">
                <v-icon :color="farm.riskColor" size="24">mdi-sprout</v-icon>
                <div style="flex:1;min-width:0;">
                  <div class="text-body-2 font-weight-bold text-truncate">{{ farm.name }}</div>
                  <div class="text-caption text--secondary text-truncate">
                    <span v-if="farm.regId">{{ farm.regId }}</span>
                    <span v-if="farm.regId && farm.location"> · </span>
                    <span v-if="farm.location">{{ farm.location }}</span>
                  </div>
                </div>
                <v-chip x-small :style="{ background: farm.chipBg, color: farm.chipColor, border: `1px solid ${farm.chipColor}` }">
                  {{ farm.riskLabel }}
                </v-chip>
              </div>
            </v-card>
          </div>
          <v-divider class="my-4" />
          <div class="mb-4">
            <v-checkbox v-model="manualApproveModal.isTemporaryApproval" color="primary" class="mt-0 pt-0" hide-details>
              <template v-slot:label>
                <div>
                  <span class="font-weight-medium black--text">{{ $t('dueDiligence.temporaryApprovalLabel') }}</span>
                  <br>
                  <span class="text-caption black--text">{{ $t('dueDiligence.temporaryApprovalDescription') }}</span>
                </div>
              </template>
            </v-checkbox>
          </div>
          <div v-if="manualApproveModal.isTemporaryApproval" class="mb-2">
            <div class="d-flex align-center mb-2">
              <span class="font-weight-medium mr-2">{{ $t('dueDiligence.approvalExpirationPeriod') }}</span>
            </div>
            <v-row no-gutters>
              <v-col cols="4">
                <v-text-field v-model.number="manualApproveModal.approvalExpirationValue" type="number" min="1" outlined dense class="mr-2" />
              </v-col>
              <v-col cols="8">
                <v-select v-model="manualApproveModal.approvalExpirationUnit"
                  :items="manualApproveExpirationUnits" item-text="text" item-value="value" outlined dense />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4 pt-0">
          <v-spacer />
          <v-btn text color="grey darken-1" :disabled="manualApproveSaving" @click="closeManualApproveModal">{{ $t('cancel') }}</v-btn>
          <ManualApproveButton
            :count="manualApproveModal.items.length"
            :disabled="manualApproveModal.isTemporaryApproval && !isManualApproveExpirationValid"
            :loading="manualApproveSaving"
            @click="saveManualApprovals"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import trafficLightAssessmentMixin from '../mixins/trafficLightAssessmentMixin'
import IYBTrafficLightPanel from '../../approval-flow/components/IYBTrafficLightPanel.vue'
import StatusHistoryModal from '../../approval-flow/components/StatusHistoryModal.vue'
import ViewGeoLocation from '../components/location-verification/ViewGeoLocation.vue'
import ManualApproveButton from '../../approval-flow/components/ManualApproveButton.vue'
import DeforestationService from '@/_services/DeforestationService'
import { getEUDRDeforestationStatus, isIndonesiaPtsiClient } from '@/utils.js'
import {
  getMergedProductionPlaceStatusDisplay,
  isCombinedHighRiskProductionPlace,
  isCombinedMediumRiskProductionPlace,
  isMitigationEvidenceApproved,
  getProductionPlaceEudrStatus,
  getRiskSnapshotFromProductionPlace,
} from '@/utils/trafficLightCalculator'
import { getDdsUpdateFarmId, throwOnGraphqlErrors, resolveMitigationApprovalStatus } from '@/utils/ddsMitigationNotification'
import { getFarmMitigationFiles } from '../mixins/trafficLightAssessmentMixin'
import { countryListIncludesCountry } from '@/constants/countryAliases'
import moment from 'moment'

export default {
  name: 'PTSIProductionPlaceTable',

  components: {
    IYBTrafficLightPanel,
    StatusHistoryModal,
    ViewGeoLocation,
    ManualApproveButton,
  },

  mixins: [trafficLightAssessmentMixin],

  props: {
    productionPlaces: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    from: { type: Number, default: 0 },
    to: { type: Number, default: 0 },
    totalProductionPlaces: { type: Number, default: 0 },
    options: {
      type: Object,
      default: () => ({ page: 1, limit: 10 }),
    },
    diligenceId: {
      required: true,
    },
    report: {
      type: Object,
      default: () => ({}),
    },
    search: {
      type: String,
      default: '',
    },
    // CFA: NACCU/Kenya final reports list the lot each production place came from.
    // Off by default so the other consumers of this table are unaffected.
    showLotId: {
      type: Boolean,
      default: false,
    },
    eudrSettings: {
      type: Object,
      default: () => ({}),
    },
    approvalExpirationValue: {
      type: Number,
      default: 30,
    },
    approvalExpirationUnit: {
      type: String,
      default: 'days',
    },
    countryOfProduction: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedItem: false,
      expandedItemId: null,
      trafficLightEvidenceLoading: false,
      manualApproveSaving: false,
      showHistoryModal: false,
      currentProductionPlaceId: null,
      viewFarmDialog: false,
      selectedProductionPlace: null,
      manualApproveCheckbox: [],
      manualApproveSelectedItems: {},
      manualApproveModal: {
        open: false,
        mode: 'single',
        ref: '',
        items: [],
        farms: [],
        summaryTL: { green: 0, manualGreen: 0, yellow: 0, red: 0, total: 0 },
        mediumCount: 0,
        highCount: 0,
        isTemporaryApproval: false,
        approvalExpirationValue: 30,
        approvalExpirationUnit: 'days',
      },
      manualApproveExpirationUnits: [
        { text: this.$t('dueDiligence.approvalExpirationDays'), value: 'days' },
        { text: this.$t('dueDiligence.approvalExpirationWeeks'), value: 'weeks' },
        { text: this.$t('dueDiligence.approvalExpirationMonths'), value: 'months' },
      ],
      menus: [
        { title: this.$t('dueDiligence.viewDeforestationAssessment'), id: 'assessment' },
        { title: this.$t('dueDiligence.viewDeforestationCertificate'), id: 'compliance' },
      ],
    }
  },

  computed: {
    ptsiHeaders() {
      return [
        { text: '', value: 'select', sortable: false, width: '48px', class: 'black--text' },
        ...(this.showLotId
          ? [{ text: this.$t('dueDiligence.lotId'), align: 'start', value: 'lotId', class: 'black--text', sortable: true }]
          : []),
        { text: this.$t('deforestation.productionPlacesFarmAndFarmId'), align: 'start', value: 'farm.farmName', class: 'black--text', sortable: true },
        { text: this.$t('deforestation.producerName'), align: 'start', value: 'farm.userDdsAssoc.firstName', class: 'black--text', sortable: true },
        { text: this.$t('deforestation.addressOfProduction'), align: 'start', value: 'farm.address', class: 'black--text', sortable: true },
        { text: this.$t('deforestation.mergedComplianceStatus'), align: 'center', value: 'mergedComplianceStatus', class: 'black--text', sortable: false },
        { text: this.$t('dueDiligence.actions'), align: 'center', value: 'actions', class: 'black--text', sortable: false },
      ]
    },
    totalPages() {
      return Math.ceil(this.totalProductionPlaces / (this.options.limit || 10))
    },
    ptsiStatusMergeOptions() {
      return { indonesiaPtsi: isIndonesiaPtsiClient() }
    },
    manualApproveEligibleOnPage() {
      return this.productionPlaces.filter(p => this.canManualApproveFarm(p))
    },
    mediumRiskEligibleOnPage() {
      return this.manualApproveEligibleOnPage.filter(p => isCombinedMediumRiskProductionPlace(p, this.ptsiStatusMergeOptions))
    },
    highRiskEligibleOnPage() {
      return this.manualApproveEligibleOnPage.filter(p => isCombinedHighRiskProductionPlace(p, this.ptsiStatusMergeOptions))
    },
    pageSelectAll() {
      const eligible = this.manualApproveEligibleOnPage
      return eligible.length > 0 && eligible.every(p => this.manualApproveCheckbox.includes(p.id))
    },
    pageIndeterminate() {
      const eligible = this.manualApproveEligibleOnPage
      const selected = eligible.filter(p => this.manualApproveCheckbox.includes(p.id)).length
      return selected > 0 && selected < eligible.length
    },
    isMediumRiskFilterChecked() {
      const farms = this.mediumRiskEligibleOnPage
      return farms.length > 0 && farms.every(p => this.manualApproveCheckbox.includes(p.id))
    },
    isMediumRiskFilterIndeterminate() {
      const farms = this.mediumRiskEligibleOnPage
      const selected = farms.filter(p => this.manualApproveCheckbox.includes(p.id)).length
      return selected > 0 && selected < farms.length
    },
    isHighRiskFilterChecked() {
      const farms = this.highRiskEligibleOnPage
      return farms.length > 0 && farms.every(p => this.manualApproveCheckbox.includes(p.id))
    },
    isHighRiskFilterIndeterminate() {
      const farms = this.highRiskEligibleOnPage
      const selected = farms.filter(p => this.manualApproveCheckbox.includes(p.id)).length
      return selected > 0 && selected < farms.length
    },
    manualApproveBulkSummary() {
      const items = Object.values(this.manualApproveSelectedItems)
      return {
        medium: items.filter(p => isCombinedMediumRiskProductionPlace(p, this.ptsiStatusMergeOptions)).length,
        high: items.filter(p => isCombinedHighRiskProductionPlace(p, this.ptsiStatusMergeOptions)).length,
      }
    },
    isManualApproveExpirationValid() {
      if (!this.manualApproveModal.isTemporaryApproval) return true
      return Number(this.manualApproveModal.approvalExpirationValue) > 0
    },
    menusWithoutCompliance() {
      return this.menus.filter(menu => menu.id !== 'compliance')
    },
  },

  watch: {
    productionPlaces() {
      if (this.trafficLightDialog && this.trafficLightItem) {
        this.refreshTrafficLightDialogItem(this.trafficLightItem.id)
      }
    },
  },

  methods: {
    // Required by trafficLightAssessmentMixin — signals parent to re-fetch data
    async getProductionPlaces() {
      this.$emit('refresh')
    },

    getEUDRDeforestationStatus(status) {
      return getEUDRDeforestationStatus(status, this.$t.bind(this))
    },

    // Merged compliance status
    mergedProductionPlaceStatus(item) {
      return getMergedProductionPlaceStatusDisplay(item, this.$t.bind(this), this.ptsiStatusMergeOptions)
    },
    mergedProductionPlaceStatusLabel(item) {
      return this.mergedProductionPlaceStatus(item)?.label ?? 'N/A'
    },
    mergedProductionPlaceStatusBadgeClass(item) {
      return this.mergedProductionPlaceStatus(item)?.badgeClass ?? 'gray-button'
    },
    mergedProductionPlaceStatusTooltip(item) {
      const eudr = this.getEUDRDeforestationStatus(item?.deforestationStatus || item?.errorStatus || '')
      const localDb = this.trafficLightLabel(item)
      return `${this.$t('deforestation.complianceStatusReview')}: ${localDb} | ${this.$t('deforestation.EUDRDeforestationStatus')}: ${eudr.text}`
    },

    // Manual approve eligibility
    canManualApproveFarm(item) {
      if (isMitigationEvidenceApproved(item)) return false
      const s = (item.overallHistoryStatus || item.deforestationStatus || item.eudr_deforestation_status || '').toLowerCase()
      if (s.includes('manually mitigated') || s.includes('manually_mitigated')) return false
      const merged = getMergedProductionPlaceStatusDisplay(item, this.$t.bind(this), this.ptsiStatusMergeOptions)
      if (merged?.riskTier === 'compliant' || merged?.riskTier === 'low') return false
      return isCombinedMediumRiskProductionPlace(item, this.ptsiStatusMergeOptions) || isCombinedHighRiskProductionPlace(item, this.ptsiStatusMergeOptions)
    },

    // Checkbox management
    toggleManualApproveFarm(item, selected) {
      if (selected) {
        if (!this.manualApproveCheckbox.includes(item.id)) this.manualApproveCheckbox.push(item.id)
        this.$set(this.manualApproveSelectedItems, item.id, item)
      } else {
        this.manualApproveCheckbox = this.manualApproveCheckbox.filter(id => id !== item.id)
        this.$delete(this.manualApproveSelectedItems, item.id)
      }
    },
    togglePageSelectAll(val) {
      const eligible = this.manualApproveEligibleOnPage
      if (val) eligible.forEach(p => this.toggleManualApproveFarm(p, true))
      else eligible.forEach(p => this.toggleManualApproveFarm(p, false))
    },
    toggleRiskSelectFilter(risk, checked) {
      let farms = []
      if (risk === 'medium') farms = this.mediumRiskEligibleOnPage
      else if (risk === 'high') farms = this.highRiskEligibleOnPage
      else farms = this.manualApproveEligibleOnPage
      farms.forEach(p => this.toggleManualApproveFarm(p, checked))
    },
    handleRiskFilterClick(risk) {
      if (risk === 'medium') {
        this.toggleRiskSelectFilter('medium', !this.isMediumRiskFilterChecked || this.isMediumRiskFilterIndeterminate)
        return
      }
      if (risk === 'high') {
        this.toggleRiskSelectFilter('high', !this.isHighRiskFilterChecked || this.isHighRiskFilterIndeterminate)
        return
      }
      const shouldSelect = !this.pageSelectAll || this.pageIndeterminate
      this.togglePageSelectAll(shouldSelect)
    },
    clearManualApproveSelection() {
      this.manualApproveCheckbox = []
      this.manualApproveSelectedItems = {}
    },

    // Manual approve modal
    buildFarmApproveDetail(item) {
      const farmName = item?.farm?.farmName || ('Farm ' + (item?.farm?.id || item?.id || ''))
      const farmRegId = item?.farm?.farmerRegId || item?.farm?.nationalFarmId || String(item?.farm?.id || item?.id || '')
      const farmLocation = item?.farm?.address || ''
      const isHigh = isCombinedHighRiskProductionPlace(item, this.ptsiStatusMergeOptions)
      const isMedium = isCombinedMediumRiskProductionPlace(item, this.ptsiStatusMergeOptions)
      return {
        id: item.id,
        name: farmName,
        regId: farmRegId,
        location: farmLocation,
        riskLabel: isHigh ? 'High Risk' : (isMedium ? 'Medium Risk' : 'Low Risk'),
        riskColor: isHigh ? 'error' : 'warning',
        chipBg: isHigh ? '#FFEBEE' : '#FFF8E1',
        chipColor: isHigh ? '#EF5350' : '#FFA826',
      }
    },
    initManualApproveModal(items) {
      if (!items?.length) return
      this.manualApproveModal = {
        ...this.manualApproveModal,
        open: true,
        mode: items.length > 1 ? 'bulk' : 'single',
        items,
        farms: items.map(i => this.buildFarmApproveDetail(i)),
        mediumCount: items.filter(i => isCombinedMediumRiskProductionPlace(i, this.ptsiStatusMergeOptions)).length,
        highCount: items.filter(i => isCombinedHighRiskProductionPlace(i, this.ptsiStatusMergeOptions)).length,
        ref: this.report?.internalReferenceNumber || this.report?.EUDRReferenceNumber || String(this.diligenceId),
        isTemporaryApproval: false,
        approvalExpirationValue: this.approvalExpirationValue,
        approvalExpirationUnit: this.approvalExpirationUnit,
      }
    },
    openManualApproveModal(item) {
      this.initManualApproveModal([item])
    },
    openBulkManualApproveModal() {
      const items = Object.values(this.manualApproveSelectedItems)
      this.initManualApproveModal(items)
    },
    closeManualApproveModal() {
      this.manualApproveModal.open = false
    },
    async saveManualApprovals() {
      if (this.manualApproveSaving) return
      const { items, isTemporaryApproval, approvalExpirationValue, approvalExpirationUnit } = this.manualApproveModal
      this.manualApproveSaving = true
      try {
        await Promise.all(items.map(async (item) => {
          const farmId = getDdsUpdateFarmId(item)
          if (!farmId) return
          const approvalStatus = resolveMitigationApprovalStatus(item)
          const res = await DeforestationService.updateDeforestationStatus({
            farm_id: [farmId],
            diligence_report_id: parseInt(this.diligenceId, 10),
            eudr_deforestation_status: approvalStatus,
            eudr_s3_key: '',
            eudr_s3_location: '',
            eudr_comment: isTemporaryApproval ? `Temporary approval for ${approvalExpirationValue} ${approvalExpirationUnit}` : '',
            isTemporaryApproval,
            approvalExpirationValue: isTemporaryApproval ? parseInt(approvalExpirationValue, 10) : null,
            approvalExpirationUnit: isTemporaryApproval ? approvalExpirationUnit : null,
          })
          throwOnGraphqlErrors(res, 'Failed to save manual approval')
        }))

        const approvedIds = items.map(i => i.id)
        this.manualApproveCheckbox = this.manualApproveCheckbox.filter(id => !approvedIds.includes(id))
        approvedIds.forEach(id => this.$delete(this.manualApproveSelectedItems, id))

        const count = items.length
        const msg = isTemporaryApproval
          ? (count === 1 ? `${items[0].farm?.farmName || 'Farm'} temporarily approved.` : `${count} farm(s) temporarily approved.`)
          : (count === 1 ? `${items[0].farm?.farmName || 'Farm'} manually approved as Manually Mitigated.` : `${count} farm(s) manually approved.`)
        this.$notify({ text: msg, type: 'success' })
        this.manualApproveModal.open = false
        await this.getProductionPlaces()
      } catch (error) {
        console.error('Failed to save manual approvals', error)
        this.$notify({ text: this.$t('somethingWentWrong'), type: 'error' })
      } finally {
        this.manualApproveSaving = false
      }
    },

    // Expand / collapse
    toggleExpand(itemId) {
      this.expandedItemId = this.expandedItemId === itemId ? null : itemId
    },
    isExpanded(itemId) {
      return this.expandedItemId === itemId
    },

    // History modal
    openHistoryModal(item) {
      const productionPlaceId = item.diligenceReportProductionPlaceArray?.[0]?.id
      if (!productionPlaceId) {
        this.$notify({ text: this.$t('dueDiligence.productionPlaceIdNotFound'), type: 'error' })
        return
      }
      this.currentProductionPlaceId = productionPlaceId
      this.showHistoryModal = true
    },
    closeHistoryModal() {
      this.showHistoryModal = false
      this.currentProductionPlaceId = null
    },

    // View farm
    openViewFarmModal(item) {
      this.selectedProductionPlace = item
      this.viewFarmDialog = true
    },
    closeViewFarmModal() {
      this.viewFarmDialog = false
      this.selectedProductionPlace = null
    },

    // Search
    onSearchInput(value) {
      this.$emit('update:search', value)
    },

    // Report button warning
    onReportButtonClick(eudrStatus) {
      if (!eudrStatus) {
        this.$notify({ text: this.$t('emptyDeforestationStatus'), type: 'error' })
      }
    },

    // Report menus
    getMenusForReport(deforestationReport) {
      return deforestationReport?.transactionHash ? this.menus : this.menusWithoutCompliance
    },

    // Expanded row helpers
    hasLocationError(item) {
      if (!item.warnings || !Array.isArray(item.warnings) || !item.warnings.length) return false
      const cop = this.countryOfProduction
      if (!cop || !Array.isArray(cop) || !cop.length) return false
      return item.warnings.some(w => w.country && !countryListIncludesCountry(cop, w.country))
    },
    hasAreaTooLargeError(item) {
      const errorStatus = item.errorStatus
        || item.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo?.deforestationReport?.errorStatus
        || item.deforestationInfo?.deforestationReport?.errorStatus
      if (errorStatus !== 'area_too_large') return false
      return item.farmType !== 'Polygon'
    },
    getDeforestationStatusDate(item) {
      return item.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo?.deforestationStatusDate
        || item.deforestationInfo?.deforestationStatusDate
        || null
    },
    getExpiryDate(issueDate) {
      if (!issueDate) return null
      const settings = this.eudrSettings || {}
      if (settings.dynamicExpiryTime && settings.dynamicExpiryTimePeriod) {
        const timeValue = settings.dynamicExpiryTime
        const timePeriod = settings.dynamicExpiryTimePeriod.toLowerCase()
        const periodMap = { day: 'days', days: 'days', week: 'weeks', weeks: 'weeks', month: 'months', months: 'months', year: 'years', years: 'years' }
        return moment(issueDate).add(timeValue, periodMap[timePeriod] || 'months').toDate()
      }
      if (settings.deforestation_assessment_validity_period) {
        return moment(issueDate).add(settings.deforestation_assessment_validity_period, 'months').toDate()
      }
      return null
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return moment(date).format('DD-MMM-YYYY')
    },
    getRiskAssessmentStatusText(status) {
      if (!status) return this.$t('dueDiligence.noRiskAssessment')
      const map = {
        approved: this.$t('dueDiligence.approved'),
        rejected: this.$t('dueDiligence.rejected'),
        mitigation_required: this.$t('dueDiligence.mitigationRequired'),
        pending: this.$t('dueDiligence.pending'),
      }
      return map[status] || status
    },
    getRiskAssessmentStatusClass(status) {
      const map = { approved: 'green-button', rejected: 'red-button', mitigation_required: 'orange-button', pending: 'yellow-button' }
      return map[status] || ''
    },
    getMitigationFiles(item) {
      const drpp = item.diligenceReportProductionPlaceArray?.[0]
      return drpp?.riskMitigationFiles || item.risk_mitigation_files || []
    },
    getMitigationComment(item) {
      const drpp = item.diligenceReportProductionPlaceArray?.[0]
      return drpp?.productionPlaceDeforestationInfo?.deforestationMitigationComment
        || item.deforestationInfo?.deforestationMitigationComment
        || null
    },
    getFileNameWithoutExtension(filePath) {
      if (!filePath) return 'N/A'
      return filePath.split('/').pop().split('?')[0].replace(/\.pdf$/i, '')
    },
    viewDocument(filePath) {
      if (filePath) window.open(filePath, '_blank')
    },
    getFarmMitigationFiles(item) {
      return getFarmMitigationFiles(item)
    },
    getMitigationFileName(filePath) {
      if (!filePath) return 'N/A'
      return filePath.split('/').pop().split('?')[0]
    },
    openMitigationFile(filePath) {
      if (filePath) window.open(filePath, '_blank')
    },
    productionPlaceEudrStatusDisplay(item) {
      if (!item) return { class: 'gray-button', text: 'N/A' }
      const rawStatus = getProductionPlaceEudrStatus(item)
      if (!rawStatus) {
        return this.getEUDRDeforestationStatus(item.errorStatus || '')
      }
      return this.getEUDRDeforestationStatus(rawStatus)
    },
    localDbComplianceDisplay(item) {
      if (!item) return { badgeClass: 'gray-button', label: 'N/A' }
      if (isMitigationEvidenceApproved(item)) {
        return { badgeClass: 'manually-button', label: this.$t('dueDiligence.manualMitigated') }
      }
      const riskSnapshot = getRiskSnapshotFromProductionPlace(item)
      if (riskSnapshot?.complianceStatus && String(riskSnapshot.complianceStatus).toLowerCase().includes('manually mitigated')) {
        return { badgeClass: 'manually-button', label: this.$t('dueDiligence.manualMitigated') }
      }
      return { badgeClass: this.trafficLightBadgeClass(item), label: this.trafficLightLabel(item) }
    },
    async approveTrafficLightEvidence(item) {
      const farmId = getDdsUpdateFarmId(item)
      if (!farmId) return
      this.trafficLightEvidenceLoading = true
      try {
        const approvalStatus = resolveMitigationApprovalStatus(item)
        const res = await DeforestationService.updateDeforestationStatus({
          farm_id: [farmId],
          diligence_report_id: parseInt(this.diligenceId, 10),
          eudr_deforestation_status: approvalStatus,
          eudr_s3_key: '',
          eudr_s3_location: '',
          eudr_comment: `Temporary mitigation evidence approval for ${this.approvalExpirationValue} ${this.approvalExpirationUnit}`,
          isTemporaryApproval: true,
          approvalExpirationValue: this.approvalExpirationValue,
          approvalExpirationUnit: this.approvalExpirationUnit,
        })
        throwOnGraphqlErrors(res, 'Failed to approve mitigation evidence')
        this.$notify({ text: `Evidence approved for ${item.farm.farmName || 'farm'}.`, type: 'success' })
        this.trafficLightDialog = false
        await this.getProductionPlaces()
      } catch (error) {
        console.error('Failed to approve mitigation evidence', error)
        this.$notify({ text: this.$t('somethingWentWrong'), type: 'error' })
      } finally {
        this.trafficLightEvidenceLoading = false
      }
    },
  },
}
</script>
